import { createSelector } from 'reselect';
import { getQueryParams } from './locationSelectors';
import { getTexts } from './textSelectors';
import { getSiteId } from './settingsSelectors';

const getFiltersState = (state) => state.filters || {};

export const getSelectedDestinations = createSelector([getQueryParams], (query) => {
  const codes = query?.destinationCode || [];
  if (Array.isArray(codes)) {
    return codes;
  }

  return [codes];
});

export const getDestinations = createSelector([getFiltersState], (s) => {
  return s.destinations || [];
});

export const getDestinationToCountryMap = createSelector([getDestinations], (destinations) => {
  return destinations.reduce((acc, country) => {
    const current = {};
    country.destinations.forEach((dest) => {
      current[dest.code] = country.countryCode;
    });

    return {
      ...current,
      ...acc,
    };
  }, {});
});

export const getPriceRange = createSelector([getFiltersState], (state) => {
  return state.price;
});

export const getSelectedPrice = createSelector([getQueryParams], (query) => {
  return query?.priceTo || null;
});

export const getTripTypes = createSelector([getFiltersState, getTexts], (state, texts) => {
  return state.tripTypes.map((tripType) => {
    return {
      ...tripType,
      label: texts.filterKeys[tripType.key],
    };
  });
});

export const getSelectedTripTypes = createSelector([getQueryParams], (query) => {
  const types = query?.tripTypes || [];
  if (Array.isArray(types)) {
    return types;
  }
  return [types];
});

export const getTotalFilteredCount = createSelector([getFiltersState], (s) => s.totalCount);

export const getTripLengths = createSelector([getFiltersState, getTexts], (s, texts) => {
  const tls = s.tripLengths || [];

  const labels = texts?.filterKeys || {};

  return tls.map((tl) => {
    return {
      ...tl,
      displayName: labels[tl.metadataKey],
    };
  });
});

const getDefaultTripLength = createSelector([getTripLengths], (tls) => {
  return tls.find((tl) => tl.value === -1);
});

export const getSelectedTripLength = createSelector(
  [getQueryParams, getTripLengths, getDefaultTripLength],
  (query, tls, defaultTripLength) => {
    const selected = tls.find((tl) => {
      return query[tl.key] === tl.value;
    });

    return selected || defaultTripLength;
  },
);

export const getAvailableDates = createSelector([getFiltersState], (s) => s.dates || []);

export const getSelectedFromDate = createSelector(
  [getAvailableDates, getQueryParams],
  (dates, query) => {
    const selected =
      query.dateFrom && dates.find((date) => date.value.toString() === query.dateFrom.toString());
    if (selected) {
      return selected;
    }

    // fallback to first available date
    return { ...dates[0], fallback: true } || {};
  },
);

export const getSelectedToDate = createSelector(
  [getAvailableDates, getQueryParams],
  (dates, query) => {
    const selected =
      query.dateTo && dates.find((date) => date.value.toString() === query.dateTo.toString());

    if (selected) {
      return selected;
    }

    // fallback to last available date
    return dates.length
      ? {
          ...dates[dates.length - 1],
          fallback: true,
        }
      : {};
  },
);

const defaultPerSite = {
  1: ['ARN'],
  3: ['OSL'],
  11: ['CPH'],
  15: ['HEL'],
  18: ['ARN'],
};

const defaultDeparture = defaultPerSite[1];

export const getDepartures = createSelector([getFiltersState], (s) => s.departures || []);

const getDeparturesAsMap = createSelector([getDepartures], (deps) => {
  return deps.reduce((acc, current) => {
    return {
      ...acc,
      [current.code]: current,
    };
  }, {});
});

export const getSelectedDepartures = createSelector(
  [getQueryParams, getSiteId, getDeparturesAsMap],
  (query, siteID, allDeps) => {
    const { departure } = query;
    let result = [];

    if (departure) {
      result = Array.isArray(departure) ? departure : [departure];
    } else if (departure === '') {
      result = [];
    } else {
      result = defaultPerSite[siteID] || defaultDeparture;
    }

    return result
      .map((code) => {
        return allDeps[code];
      })
      .filter((d) => Boolean(d));
  },
);

export const getCurrentFilterParams = createSelector(
  [getQueryParams, getSiteId],
  (query, siteID) => {
    const { departure, ...rest } = query;

    return {
      departure: departure || defaultPerSite[siteID],
      ...rest,
    };
  },
);

// Loop over all destinations or countries in destinationCode and finds there countryCode
// then it filter out unique values
export const getSelectedCountryCodes = createSelector(
  [getSelectedDestinations, getDestinationToCountryMap],
  (selected, destinations) => {
    return selected
      .filter((mayBeCountry) => !destinations[mayBeCountry])
      .filter((v, i, self) => self.indexOf(v) === i);
  },
);

// Same as `getSelectedCountryCodes` but filter out destinations, not countries
export const getSelectedDestinationCodes = createSelector(
  [getSelectedDestinations, getDestinationToCountryMap],
  (selected, destinations) => {
    return selected
      .filter((mayBeCountry) => destinations[mayBeCountry])
      .filter((v, i, self) => self.indexOf(v) === i);
  },
);
